const content = `
# Model Training

The training pipeline builds LightGBM regression models for each combination of component, building type, and cluster. Models learn to predict hourly kW from weather, calendar, and building metadata features.

## Training Architecture

For each building type, buildings are grouped into clusters (default 4) based on load shape similarity. Then for each **component x building_type x cluster** combination, a separate LightGBM model is trained. A typical training run produces 100-200 individual models.

\`\`\`
Components (4)  x  Building Types (9)  x  Clusters (4)  =  144 models
\`\`\`

## Data Splits

Buildings are split by building ID (not by hour) to prevent data leakage:

| Split | Fraction | Purpose |
|-------|----------|---------|
| **Train** | 70% | Model fitting |
| **Validation** | 15% | Optuna hyperparameter selection |
| **Test** | 15% | Final evaluation (never seen during training) |

## Optuna Hyperparameter Tuning

Each model uses **Optuna** (Bayesian optimization) to search the LightGBM hyperparameter space. Key parameters tuned:

- \`num_leaves\` — Tree complexity
- \`learning_rate\` — Step size
- \`n_estimators\` — Number of boosting rounds
- \`min_child_samples\` — Minimum data per leaf
- \`subsample\` — Row sampling fraction
- \`colsample_bytree\` — Feature sampling fraction

The default is 50 trials per model, adjustable in the training form.

## Training Form Parameters

| Parameter | Default | Description |
|-----------|---------|-------------|
| **Version Name** | \`v1\` | Unique model version identifier |
| **n_buildings** | 3000 | Number of sampled buildings to train on |
| **optuna_trials** | 50 | Hyperparameter search trials per model |
| **n_clusters** | 4 | Clusters per building type |

## Training Progress

Training runs as a background job on the PhD API. Progress phases:

1. **Loading** — Read sampled buildings and profiles from cache
2. **Clustering** — Compute archetype clusters per building type
3. **Training** — Train models (this is the longest phase; progress shows component/type/cluster)
4. **Saving** — Write model artifacts and manifest to the model registry

## Model Registry

Trained models are saved to the ComStock cache directory under:

\`\`\`
.comstock_cache/models/{version}/
    manifest.json          — Version metadata, component list, metrics summary
    {component}/{type}/{cluster}/model.lgb   — Serialized LightGBM model
    {component}/{type}/{cluster}/metadata.json — Feature importance, test metrics
\`\`\`

The **Training** tab displays all available model versions. Click a version to view its details, or click **Activate** to set it as the version used for validation and forecasting.
`;

export default content;
